/**
 * "Gwil means to take your leg. Allow it?" (M4-05).
 *
 * The one prompt in the game the *world* opens rather than the player. Every
 * other menu here is something you asked for by pressing a key; this one
 * arrives because somebody knelt over you with an axe and asked a question, and
 * it takes the keyboard until you answer it — which is the right precedence,
 * because nothing else you could be doing matters as much.
 *
 * It is driven off the log stream rather than off a snapshot field, and that is
 * deliberate: the request is an *event* addressed to one person (the server
 * sends it with `audience: 'actor'`, so it reaches nobody else's client), and a
 * question is a thing that happens at a moment. A snapshot flag would have made
 * it a state the UI had to poll and reconcile, and would have re-opened the
 * modal every frame until it was answered.
 *
 * Pure state, like the aim and interact menus: no DOM, no socket.
 */

import type { LogEvent } from '@annwn/protocol';

/** The keys, and why: `y` allows, `n` refuses, anything else is a refusal too. */
export const ALLOW_KEY = 'y';
export const REFUSE_KEY = 'n';

export interface ConsentRequest {
  /** Who asked. The answer names them, because two people may want the leg. */
  readonly surgeonId: string;
  readonly surgeonName: string;
  readonly part: string;
}

/** What the answer resolves to — the payload of a `consent` intent. */
export interface ConsentAnswer {
  readonly surgeonId: string;
  readonly allow: boolean;
}

/** The line the player reads. Plain, and it names the limb. */
export function consentPrompt(request: ConsentRequest): string {
  return `${request.surgeonName} means to take your ${request.part}. Allow it? (y / n)`;
}

/**
 * Is this event the question?
 *
 * Matched on the tuple rather than on the prose, for the reason the log pane
 * matches on tuples: the sentence is a view and the refs are the record, and a
 * client that keyed off the wording would break the day somebody rewrote it.
 */
export function isConsentRequest(event: LogEvent, viewerId: string): boolean {
  return event.refs[0] === 'amputate' && event.refs[1] === 'asks' && event.actors[1] === viewerId;
}

export class ConsentPrompt {
  private request: ConsentRequest | null = null;

  get active(): boolean {
    return this.request !== null;
  }

  get pending(): ConsentRequest | null {
    return this.request;
  }

  /**
   * Watch the log for a question addressed to this viewer.
   *
   * Returns the line to show, or `null`. The *last* request in a batch wins:
   * if two people asked in the same second, the one still standing over you is
   * the one whose question is live.
   */
  observe(
    events: readonly LogEvent[],
    viewerId: string,
    nameOf: (id: string) => string,
  ): string | null {
    let prompt: string | null = null;

    for (const event of events) {
      if (!isConsentRequest(event, viewerId)) continue;
      const surgeonId = event.actors[0] ?? '';
      this.request = {
        surgeonId,
        surgeonName: nameOf(surgeonId),
        part: event.refs[2] ?? 'limb',
      };
      prompt = consentPrompt(this.request);
    }

    return prompt;
  }

  close(): void {
    this.request = null;
  }

  /**
   * Feed it a key.
   *
   * Anything that is not `y` is a refusal, and that asymmetry is the whole
   * safety property: a mistyped keystroke can never cost somebody a leg, and
   * the player who wants to lose one has to say so.
   */
  answer(key: string): ConsentAnswer | null {
    const request = this.request;
    if (!request) return null;

    this.close();
    return { surgeonId: request.surgeonId, allow: key.toLowerCase() === ALLOW_KEY };
  }
}
